import { createRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSession } from "@/auth/client";
import { AppLayout } from "@/components/layout/app-layout";
import { canAccessPath } from "@/lib/team-access";
import { Route as rootRoute } from "./__root";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [ownerId, setOwnerId] = useState<string | null | "loading">("loading");

  const isAdmin = session?.user?.role === "admin";

  useEffect(() => {
    if (!session?.user || isAdmin) {
      setOwnerId(null);
      return;
    }
    fetch("/api/me/owner")
      .then((r) => r.json())
      .then((data: { owner?: { id?: string } | null }) => {
        setOwnerId(data.owner?.id ?? null);
      })
      .catch(() => setOwnerId(null));
    // biome-ignore lint/correctness/useExhaustiveDependencies: session?.user is the correct dep
  }, [session?.user, isAdmin]);

  if (isPending || (!isAdmin && ownerId === "loading")) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!session?.user) {
    navigate({ to: "/" });
    return null;
  }

  // Non-admins: redirect away from pages they can't access
  if (!isAdmin && ownerId !== "loading") {
    const allowed = canAccessPath(ownerId ?? "", pathname, session.user.email);
    if (!allowed) {
      navigate({ to: "/my-dashboard" });
      return null;
    }
  }

  return (
    <AppLayout ownerId={isAdmin ? null : (ownerId ?? null)}>
      <Outlet />
    </AppLayout>
  );
}
